import React from 'react';
import { Phone, Mail, MapPin, Facebook, Instagram, Lock, Building2, UserPlus } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { AGENCY_INFO, LOGO_URL, AGENCY_SEAL } from '../constants';

const Footer = () => {
    const navigate = useNavigate();

    const handleNavigation = (path) => {
        navigate(path);
        window.scrollTo(0, 0);
    };

    return (
        <footer className="bg-[#2C353D] text-white py-16 px-4 border-t-8 border-[#EA723D] font-sans">
            <div className="max-w-7xl mx-auto grid md:grid-cols-4 gap-12 text-sm">
                <div className="col-span-1 md:col-span-2">
                    <div className="flex items-center gap-4 mb-8">
                        <div 
                            onClick={() => handleNavigation('/')} 
                            className="bg-white p-1 rounded-full overflow-hidden shadow-xl border-4 border-[#EA723D]/20 cursor-pointer hover:scale-105 transition-transform duration-300"
                            title="Retour à l'accueil"
                        >
                            <img src={AGENCY_SEAL} alt="Borbiconi Immobilier Agence" className="h-40 w-40 object-contain" />
                        </div>
                    </div>
                    <p className="text-gray-400 mb-6 leading-relaxed max-w-sm">
                        <strong className="text-white">Votre partenaire immobilier frontalier.</strong><br />
                        Expertise et proximité à Ottange, Dudelange, Rumelange, Fontoy et dans tout le secteur frontalier.<br />
                        Gérant : <strong className="text-white">Christophe Borbiconi</strong>
                    </p>
                    <div className="flex gap-4">
                        <a
                            href={AGENCY_INFO.facebookPage}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-[#3D4852] rounded-full flex items-center justify-center text-gray-400 hover:bg-[#EA723D] hover:text-white transition-colors cursor-pointer"
                            aria-label="Facebook"
                        >
                            <Facebook size={20} />
                        </a>

                        <a
                            href={AGENCY_INFO.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="w-10 h-10 bg-[#3D4852] rounded-full flex items-center justify-center text-gray-400 hover:bg-[#EA723D] hover:text-white transition-colors cursor-pointer"
                            aria-label="Instagram"
                        >
                            <Instagram size={20} />
                        </a>
                    </div>
                </div>

                <div>
                    <h4 className="font-bold mb-6 text-[#EA723D] uppercase tracking-widest text-xs">Contact Agence</h4>
                    <ul className="space-y-4 text-gray-400">
                        <li className="flex items-center gap-3"><Phone size={16} className="text-[#EA723D]" /> {AGENCY_INFO.phone}</li>
                        <li className="flex items-center gap-3"><Mail size={16} className="text-[#EA723D]" /> {AGENCY_INFO.email}</li>
                        <li className="flex items-center gap-3"><MapPin size={16} className="text-[#EA723D]" /> {AGENCY_INFO.address}</li>
                    </ul>
                </div>

                <div className="space-y-6">
                    <div>
                        <h4 className="font-bold mb-6 text-[#EA723D] uppercase tracking-widest text-xs">Liens Rapides</h4>
                        <ul className="space-y-3 text-gray-400 cursor-pointer font-medium mb-6">
                            <li className="hover:text-white transition-colors" onClick={() => handleNavigation('/')}>Nos Biens en vente</li>
                            <li className="hover:text-white transition-colors" onClick={() => handleNavigation('/estimation')}>Demander une estimation</li>
                            <li className="hover:text-white transition-colors" onClick={() => handleNavigation('/contact')}>Nous contacter</li>
                            <li className="hover:text-white transition-colors" onClick={() => handleNavigation('/honoraires')}>Honoraires & Barème</li>
                        </ul>
                    </div>

                    <div className="space-y-3">
                        <a 
                            href="/Candidat-Locataire-SCBI.pdf" 
                            target="_blank" 
                            className="group flex items-center gap-3 bg-white hover:bg-white/90 p-3 rounded-xl transition-all duration-300 shadow-lg border border-[#EA723D]/20 active:scale-95"
                        >
                            <div className="w-8 h-8 rounded-lg bg-[#EA723D] flex items-center justify-center text-white shadow-md shadow-orange-200">
                                <UserPlus size={18} />
                            </div>
                            <div className="flex flex-col">
                                <span className="text-[10px] text-gray-500 uppercase font-black tracking-widest leading-none mb-1">Espace Location</span>
                                <span className="text-[12px] font-bold text-[#2C353D] leading-tight">Candidat Locataire</span>
                            </div>
                        </a>

                        <div className="pt-2">
                            <a 
                                href="https://scbi.eu" 
                                target="_blank" 
                                rel="noopener noreferrer" 
                                className="group flex items-center gap-3 bg-[#3D4852] hover:bg-[#EA723D]/20 p-3 rounded-xl transition-all duration-300 border border-white/5 hover:border-[#EA723D]/30 active:scale-95"
                            >
                                <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center text-[#EA723D] group-hover:text-white transition-colors">
                                    <Building2 size={18} />
                                </div>
                                <div className="flex flex-col">
                                    <span className="text-[10px] text-gray-400 group-hover:text-white/80 uppercase font-black tracking-widest leading-none mb-1">Découvrir le groupe</span>
                                    <span className="text-[11px] font-bold text-white transition-colors group-hover:text-white leading-tight">Portail SCBI Europe</span>
                                </div>
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto mt-16 pt-8 border-t border-gray-800 flex flex-col md:flex-row justify-between items-center text-gray-600 text-xs gap-4">
                <div className="flex flex-col md:flex-row gap-2 items-center">
                    <span>© {new Date().getFullYear()} Borbiconi Immobilier • Tous droits réservés.</span>
                    <span className="hidden md:inline text-gray-800">•</span>
                    <a href="https://kaubry.fr" target="_blank" rel="noopener noreferrer" className="hover:text-[#EA723D] transition-colors font-medium">
                        Propulsé par <span className="font-bold">KauBry Apps</span>
                    </a>
                </div>
                <div className="flex gap-6">
                    <Link to="/mentions-legales" onClick={() => window.scrollTo(0, 0)} className="hover:text-gray-400 cursor-pointer">Mentions Légales</Link>
                    <Link to="/mentions-legales" onClick={() => setTimeout(() => document.getElementById('privacy')?.scrollIntoView({ behavior: 'smooth' }), 100)} className="hover:text-gray-400 cursor-pointer">Politique de confidentialité</Link>
                    <Link to="/admin/login" className="flex items-center gap-1 text-gray-600 hover:text-[#EA723D] transition-colors">
                        <Lock size={12} /> Administration
                    </Link>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
